<template>
  <div ref="dropdownWrapper" class="assignee-dropdown-wrapper">
    <!-- Search Input -->
    <div 
      class="assignee-dropdown-input"
      :style="inputStyle"
    >
      <input
        ref="searchInput"
        v-model="searchTerm"
        type="text"
        :placeholder="placeholder"
        class="search-input"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </div>

    <!-- Dropdown List -->
    <div 
      v-if="showDropdown" 
      class="dropdown-list"
      :style="dropdownStyle"
      @scroll="handleScroll"
    >
      <!-- Show results if available -->
      <div
        v-for="(item, index) in filteredOptions"
        :key="item[valueFieldName] || index"
        class="dropdown-item"
        @mousedown.prevent="selectItem(item)"
      >
        {{ item[labelFieldName] }}
      </div>
      
      <!-- Show loading state -->
      <div v-if="loading" class="dropdown-item loading-item">
        <span class="loading-text">Loading...</span>
      </div>
      
      <!-- Show "No results found" only when not loading and user has typed something -->
      <div v-if="!loading && searchTerm && filteredOptions.length === 0" class="dropdown-item disabled">
        No results found
      </div>
      
      <!-- Show "Start typing to search" when no search term and no results -->
      <div v-if="!loading && !searchTerm && filteredOptions.length === 0" class="dropdown-item disabled">
        Start typing to search
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AssigneeDropdown',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    initialValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Search...'
    },
    options: {
      type: Array,
      default: () => []
    },
    labelFieldName: {
      type: String,
      default: 'label'
    },
    valueFieldName: {
      type: String,
      default: 'value'
    },
    height: {
      type: String,
      default: '40px'
    },
    padding: {
      type: String,
      default: '8px 12px'
    },
    maxDropdownHeight: {
      type: String,
      default: '200px'
    },
    loading: {
      type: Boolean,
      default: false
    },
    canScrollFetchMore: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'search', 'scroll-bottom'],
  data() {
    return {
      searchTerm: '',
      showDropdown: false,
      isFocused: false,
      searchTimeout: null,
      isScrolling: false
    };
  },
  computed: {
    inputStyle() {
      return {
        height: this.height,
        padding: this.padding
      };
    },
    dropdownStyle() {
      return {
        maxHeight: this.maxDropdownHeight
      };
    },
    selectedItem() {
      if (!this.modelValue) return null;
      return this.options.find(item => item[this.valueFieldName] === this.modelValue);
    },
    filteredOptions() {
      return this.options;
    }
  },
  watch: {
    modelValue(newVal) {
      if (!newVal) {
        // Clear search term when modelValue is cleared
        this.searchTerm = '';
      } else {
        // Update search term to match selected item when modelValue changes
        const selectedItem = this.options.find(item => item[this.valueFieldName] === newVal);
        if (selectedItem) {
          this.searchTerm = selectedItem[this.labelFieldName];
        }
      }
    },
    initialValue: {
      immediate: true,
      handler(newVal, oldVal) {
        // Always sync search term with initialValue when it changes
        // This ensures sync between node and editor components
        if (newVal !== oldVal) {
          this.searchTerm = newVal || '';
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    handleInput() {
      // Debounce search with 300ms delay
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.$emit('search', this.searchTerm);
      }, 300);
    },
    
    handleScroll(event) {
      if (!this.canScrollFetchMore || this.isScrolling) return;
      
      const target = event.target;
      const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      
      // Trigger when scrolled to within 10px of bottom
      if (scrollBottom < 10) {
        this.isScrolling = true;
        this.$emit('scroll-bottom');
        
        // Reset scrolling flag after a delay
        setTimeout(() => {
          this.isScrolling = false;
        }, 500);
      }
    },
    
    clearInput() {
      this.searchTerm = '';
      this.$emit('update:modelValue', '');
      this.showDropdown = false;
    },
    handleFocus() {
      this.isFocused = true;
      this.showDropdown = true;
      
      // Don't trigger search on focus - only show existing results
      // Search is only triggered by actual input changes in handleInput
    },
    handleBlur() {
      this.isFocused = false;
      // Delay to allow click on dropdown item
      setTimeout(() => {
        if (!this.isFocused) {
          this.showDropdown = false;
        }
      }, 200);
    },
    selectItem(item) {
      this.$emit('update:modelValue', item[this.valueFieldName]);
      // Set search term to selected item name
      this.searchTerm = item[this.labelFieldName];
      this.showDropdown = false;
      this.$refs.searchInput.blur();
    },
    handleClickOutside(event) {
      if (this.$refs.dropdownWrapper && !this.$refs.dropdownWrapper.contains(event.target)) {
        this.showDropdown = false;
      }
    }
  }
};
</script>

<style scoped>
.assignee-dropdown-wrapper {
  position: relative;
  width: 100%;
}

.assignee-dropdown-input {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  cursor: text;
}

.assignee-dropdown-input:focus-within {
  border-color: #004879;
}

.search-input {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #333;
}

.search-input::placeholder {
  color: #999;
}

.selected-value {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: inherit;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #333;
  background: #FFFFFF;
  pointer-events: none;
}

.dropdown-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #FFFFFF;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  z-index: 1000;
}

.dropdown-list.no-results {
  max-height: auto;
}

.dropdown-item {
  padding: 10px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #333;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-item:hover:not(.disabled) {
  background-color: #F5F5F5;
}

.dropdown-item.disabled {
  color: #999;
  cursor: default;
}

.dropdown-item.loading-item {
  text-align: center;
  color: #6B7280;
  cursor: default;
}

.loading-text {
  font-style: italic;
}
</style>
